import {useCallback, useEffect, useRef, useState} from 'react';
import {
    ContactInfoFormState,
    FIXED_SCREENS,
    YCLIENTS_RECORD_TYPE,
    PhoneVerificationStatus,
    PhoneVerificationController,
    BlsFormController,
} from '@target-web/leadads-form-view';
import {bridge} from 'utils/bridge';
import {BlsDataType, FORM_TYPE, LeadFormDataType, SurveyDataType, YClientsDataType} from 'api/types/LeadForm';
import useAnalytics from './useAnalytics';
import {useUserInfo} from 'utils/userInfoContext';
import {useSearchParams} from 'utils/searchParamsContext';
import {formatPhone, isOKClient} from 'utils/utils';
import {checkCaptcha, processError, sendBlsLead, trySendLead} from 'api/services/leads';
import {LeadFormControllerInner} from 'core/types';
import {fetchVkCapthaInfo} from 'utils/requests';
import {CHECK_CAPTCHA_STATUS, VkCapthaInfoResponse} from 'utils/types';
import {useAppearance} from 'utils/appearanceContext';
import {captureException} from '@sentry/react';
import {PAGE_TYPE} from 'api/types/types';

type OnTransition = {
    isBack: boolean;
    from: string;
    to: string;
};

type dataByType =
    | {
          type: FORM_TYPE.leadform;
          formData: LeadFormDataType;
      }
    | {
          type: FORM_TYPE.survey;
          formData: SurveyDataType;
      }
    | {
          type: FORM_TYPE.yclients;
          formData: YClientsDataType;
      }
    | {
          type: FORM_TYPE.bls;
          formData: BlsDataType;
      };

const stepScreenMap = {
    master: FIXED_SCREENS.YCLIENTS_MASTER,
    service: FIXED_SCREENS.YCLIENTS_SERVICE,
    datetime: FIXED_SCREENS.YCLIENTS_DATETIME,
};

const steps = ['master', 'service', 'datetime'] as const;

type ScreenAdditionalParams = {
    initialServiceId: number | null;
};

export const getFirstYClientsScreen = (data: YClientsDataType['data'], params?: ScreenAdditionalParams) => {
    if (data.is_quick_write_enable) return FIXED_SCREENS.YCLIENTS_QUICK_WRITE;
    if (params?.initialServiceId) {
        return FIXED_SCREENS.YCLIENTS_DATETIME;
    } else if (params?.initialServiceId === 0) {
        return FIXED_SCREENS.YCLIENTS_SERVICE;
    }
    if (data.company.record_type_id === YCLIENTS_RECORD_TYPE.mix) return FIXED_SCREENS.YCLIENTS_MIX;
    if (data.company.record_type_id === YCLIENTS_RECORD_TYPE.activity) return FIXED_SCREENS.YCLIENTS_ACTIVITIES;
    if (data.company.record_type_id === YCLIENTS_RECORD_TYPE.individual) {
        if (data.form.sequence_type === 'menu') {
            return FIXED_SCREENS.YCLIENTS_INDIVIDUAL;
        }
        // TODO копипаста
        const firstStep = data.form.steps.filter((s) => steps.includes(s.step)).sort((a, b) => a.num - b.num)[0];

        if (!firstStep) {
            // TODO первый шаг для индивидуальных услуг
            throw new Error('Не найден первый шаг формы');
        }
        return stepScreenMap[firstStep.step];
    }
    throw new Error('Не найден первый шаг формы');
};

function getFirstPanelId({formData, type}: dataByType, params?: ScreenAdditionalParams): FIXED_SCREENS {
    if (formData.is_filled_previously) return FIXED_SCREENS.ALREADY_FILLED;

    if (type === 'survey') {
        return formData.page.type === PAGE_TYPE.result_info // если опрос заполнен
            ? FIXED_SCREENS.SUCCESS // кидаем юзера на результат
            : FIXED_SCREENS.MAIN_SURVEY; // иначе на главную
    } else if (type === 'bls') {
        return FIXED_SCREENS.BLS;
    } else if (type === 'yclients') {
        return getFirstYClientsScreen(formData.data, params);
    }

    return formData.agreement_only
        ? FIXED_SCREENS.AGREEMENT // показываем только соглашение
        : formData.landing
        ? FIXED_SCREENS.LEADFORM_LANDING // на лендинг
        : FIXED_SCREENS.ONE_PAGE; // иначе лидформу на ванпейдж;
}

type useNavigationProps = {
    formData: LeadFormDataType;
    formController: LeadFormControllerInner;
    phoneVerificationController: PhoneVerificationController;
    canSendSocialMediaProfile: boolean;
};

type useSurveyNavigationProps = {
    formData: SurveyDataType;
    processNextQuestionOrResult?: () => Promise<'' | 'success' | 'error'>;
};

type useBlsNavigationProps = {
    formData: BlsDataType;
    formController: BlsFormController & {
        validate: () => {
            isValid: boolean;
            emptyAnswersIds: string[];
        };
    };
};

type useYClientsNavigationProps = {
    formData: YClientsDataType;
    processNextQuestionOrResult?: () => Promise<'' | 'success' | 'error'>;
    initialServiceId: number | null;
};

function useNavigation(data: dataByType, params?: ScreenAdditionalParams) {
    const [isBack, setIsBack] = useState(false);
    const [history, setHistory] = useState<Array<FIXED_SCREENS>>(() => [getFirstPanelId(data, params)]);
    const {openPageAnalyticEvent, closeFormAnalyticEvent} = useAnalytics();

    const isFirstPanel = history.length === 1;
    const isFirstRender = useRef(true);

    useEffect(() => {
        let name = history[0];

        if (!name) return;

        if (isFirstRender.current) {
            isFirstRender.current = false;
        } else {
            name = getFirstPanelId(data, params);
            setHistory([name]);
        }

        openPageAnalyticEvent(name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.formData]);

    const goToPage = useCallback((name: FIXED_SCREENS, clear = false) => {
        setIsBack(false);
        window.history.pushState({panel: name}, name);

        // если юзер попал на страницу успешной отправки или вернули его на начало -
        // то выставляем только ее чтобы не было возможности свайпать назад
        setHistory((history) =>
            name === FIXED_SCREENS.SUCCESS ||
            name === FIXED_SCREENS.YCLIENTS_SUCCESS ||
            name === FIXED_SCREENS.YCLIENTS_QUICK_WRITE_SUCCESS ||
            name === FIXED_SCREENS.YCLIENTS_SIGNIN_ERROR ||
            clear
                ? [name]
                : [...history, name],
        );
    }, []);

    const goToFirstPage = useCallback(() => {
        goToPage(getFirstPanelId(data, params), true);
    }, [data, goToPage, params]);

    const onTransition = useCallback(
        ({to, from, isBack}: OnTransition) => {
            openPageAnalyticEvent(to, from, isBack);
        },
        [openPageAnalyticEvent],
    );

    const closeForm = useCallback(() => {
        if (bridge && bridge.supports('VKWebAppClose')) {
            closeFormAnalyticEvent();

            bridge.sendWithCatch('VKWebAppClose').catch(() => {
                bridge && bridge.sendWithCatch('VKWebAppClose', {status: 'success'}).catch(() => {});
            });
        }
    }, [closeFormAnalyticEvent]);

    const goBack = useCallback(() => {
        if (history.length === 1) {
            closeForm();
        } else if (history.length > 1) {
            setIsBack(true);
            setHistory(history.slice(0, history.length - 1));
        }
    }, [history, closeForm]);

    useEffect(() => {
        if (
            bridge &&
            !isOKClient() &&
            bridge.supports('VKWebAppSetSwipeSettings') &&
            bridge.supports('VKWebAppEnableSwipeBack') &&
            bridge.supports('VKWebAppDisableSwipeBack')
        ) {
            bridge.sendWithCatch('VKWebAppSetSwipeSettings', {history: isFirstPanel}).catch(() => {});
            bridge.sendWithCatch(isFirstPanel ? 'VKWebAppEnableSwipeBack' : 'VKWebAppDisableSwipeBack').catch(() => {});
        }
    }, [isFirstPanel]);

    useEffect(() => {
        window.addEventListener('popstate', goBack);

        return () => {
            window.removeEventListener('popstate', goBack);
        };
    }, [goBack]);

    return {
        isBack,
        isFirstPanel,
        history,
        goToPage,
        onTransition,
        closeForm,
        goBack,
        goToFirstPage,
    };
}

export function useYClientsNavigation({formData, initialServiceId}: useYClientsNavigationProps) {
    const {history, isBack, goToPage, onTransition, closeForm, goBack, goToFirstPage} = useNavigation(
        {
            formData,
            type: FORM_TYPE.yclients,
        },
        {
            initialServiceId,
        },
    );

    const goToMasters = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_MASTER);
    }, [goToPage]);
    const goToDateTime = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_DATETIME);
    }, [goToPage]);
    const goToService = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_SERVICE);
    }, [goToPage]);
    const goToPlace = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_PLACE);
    }, [goToPage]);
    const goToBookCkeck = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_CHECK);
    }, [goToPage]);
    const goToIndividual = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_INDIVIDUAL);
    }, [goToPage]);
    const goToActivities = useCallback(() => {
        goToPage(FIXED_SCREENS.YCLIENTS_ACTIVITIES);
    }, [goToPage]);

    return {
        goBack,
        goToPage,
        goToFirstPage,
        closeForm,
        onTransition,
        history,
        isBack,
        goToSpecific: {
            goToMasters,
            goToDateTime,
            goToService,
            goToBookCkeck,
            goToPlace,
            goToIndividual,
            goToActivities,
        },
    };
}

export function useSurveyNavigation({formData, processNextQuestionOrResult}: useSurveyNavigationProps) {
    const {history, goToPage, onTransition, closeForm, goBack} = useNavigation({formData, type: FORM_TYPE.survey});

    const goNext = useCallback(() => {
        const currentPage = history[history.length - 1];

        if (currentPage === FIXED_SCREENS.MAIN_SURVEY) {
            // если на первой странице опроса
            goToPage(FIXED_SCREENS.SURVEY);
        } else if (currentPage === FIXED_SCREENS.SURVEY) {
            processNextQuestionOrResult &&
                processNextQuestionOrResult().then((res) => {
                    if (res === 'success') {
                        goToPage(FIXED_SCREENS.SUCCESS);
                    }
                });
        } else if ([FIXED_SCREENS.SUCCESS, FIXED_SCREENS.ALREADY_FILLED].includes(currentPage)) {
            // на финальной странице или уже заполняли форму
            closeForm();
        }
    }, [history, goToPage, closeForm, processNextQuestionOrResult]);

    return {
        goBack,
        goToPage,
        closeForm,
        goNext,
        onTransition,
        history,
    };
}

export function useBlsNavigation({formData, formController}: useBlsNavigationProps) {
    const {formDataHash} = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const {history, goToPage, onTransition, closeForm, goBack} = useNavigation({formData, type: FORM_TYPE.bls});

    const sendLead = useCallback(async () => {
        setIsLoading(true);
        try {
            const requestData = {
                answers: Object.keys(formController.formState.answers).map((blockId) => {
                    const {value, page_id} = formController.formState.answers[blockId];
                    return {
                        survey_id: formData.id,
                        page_id,
                        block_id: blockId,
                        ...value,
                    };
                }),
            };

            const response = await sendBlsLead(formDataHash, JSON.stringify(requestData));

            if (response.ok) {
                goToPage(FIXED_SCREENS.BLS_SUCCESS);
                formData.is_filled_previously = true;
                return;
            }

            const error = await processError(response);
            throw new Error(error);
        } catch (e) {
            console.error('Не удалось сохранить лид');
        }
        setIsLoading(false);
    }, [formDataHash, formController.formState, formData, goToPage]);

    const goNext = useCallback(() => {
        const currentPage = history[history.length - 1];

        if (currentPage === FIXED_SCREENS.BLS) {
            const {isValid, emptyAnswersIds = []} = formController?.validate() ?? {};

            if (isValid) {
                sendLead();
            } else {
                if (emptyAnswersIds.length) {
                    const [firstAnswerId] = emptyAnswersIds;
                    firstAnswerId &&
                        formController?.formState?.answersRefs?.[firstAnswerId]?.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                }
            }
        } else if (currentPage === FIXED_SCREENS.BLS_SUCCESS) {
            closeForm();
        }
    }, [formController, closeForm, sendLead, history]);

    return {
        history,
        isLoading,
        goNext,
        onTransition,
        closeForm,
        goBack,
    };
}

export function useLeadFormNavigation({
    formData,
    formController,
    phoneVerificationController,
    canSendSocialMediaProfile = false,
}: useNavigationProps) {
    const {history, goToPage, onTransition, closeForm, goBack} = useNavigation({formData, type: FORM_TYPE.leadform});
    const userInfo = useUserInfo();
    const {formDataHash} = useSearchParams();
    const appearance = useAppearance();
    const [isLoading, setIsLoading] = useState(false);
    const {closeCaptchaAnalyticEvent} = useAnalytics();

    const verifyPhone = useCallback(() => {
        if (!formController.formState.contactInfo.phone) return;
        phoneVerificationController.setPhoneForCheckingVerificationStatus({
            value: formatPhone(String(formController.formState.contactInfo.phone.value)),
            hash: formController.formState.contactInfo.phone.hash,
        });
        goToPage(FIXED_SCREENS.PHONE_VERIFICATION);
    }, [formController, phoneVerificationController, goToPage]);

    const sendLead = useCallback(async () => {
        setIsLoading(true);

        let info: VkCapthaInfoResponse | null = null;
        // смотрим нужна ли капча
        try {
            info = await fetchVkCapthaInfo(formDataHash);
        } catch (error) {
            captureException(error);
        }

        if (!info) {
            setIsLoading(false);
            return;
        }

        let captchaToken: string | null = null;

        // если нужна проверка по капче - используем только ее, игнорируя проверку номера телефона
        if (info.show_captcha) {
            const checkCaptchaResult = await checkCaptcha(info.captcha_session.captcha_url, appearance);

            if (checkCaptchaResult.status !== CHECK_CAPTCHA_STATUS.success) {
                if (checkCaptchaResult.status === CHECK_CAPTCHA_STATUS.cancel) {
                    closeCaptchaAnalyticEvent();
                }
                setIsLoading(false);
                return;
            }

            captchaToken = checkCaptchaResult.token;
        } else if (
            // иначе проверим нужна ли верификация телефона
            formData.is_enable_phone_confirmation &&
            formData.contact_fields.includes('phone') &&
            phoneVerificationController.phoneVerificationState.status !== PhoneVerificationStatus.verified
        ) {
            // если нужна - отправляем юзера на страницу верификации номера
            setIsLoading(false);
            verifyPhone();
            return;
        }

        // и шлем лида
        try {
            await trySendLead({
                formDataHash,
                formData,
                formState: formController.formState,
                consentStatus: formController.consentStatus,
                userInfo,
                canSendSocialMediaProfile,
                ...(captchaToken && info.show_captcha
                    ? {
                          captchaData: {
                              session_token: info.captcha_session.session_token,
                              captcha_token: captchaToken,
                          },
                      }
                    : {}),
            });
            goToPage(FIXED_SCREENS.SUCCESS);
        } catch (e) {
            console.error('Не удалось сохранить лида');
        }
        setIsLoading(false);
    }, [
        formDataHash,
        formData,
        phoneVerificationController.phoneVerificationState.status,
        appearance,
        verifyPhone,
        formController.formState,
        formController.consentStatus,
        userInfo,
        canSendSocialMediaProfile,
        goToPage,
        closeCaptchaAnalyticEvent,
    ]);

    const goNext = useCallback(() => {
        const currentPage = history[history.length - 1];

        if (currentPage === FIXED_SCREENS.LEADFORM_LANDING) {
            goToPage(FIXED_SCREENS.ONE_PAGE);
        } else if (currentPage === FIXED_SCREENS.ONE_PAGE || currentPage === FIXED_SCREENS.PHONE_VERIFICATION) {
            const {isValid, invalidContactsFields = [], emptyAnswersIds = []} = formController?.validate() ?? {};
            if (isValid) {
                sendLead();
            } else {
                if (invalidContactsFields.length) {
                    const sortFields = [...formData.contact_fields] as (keyof ContactInfoFormState)[];
                    invalidContactsFields.sort((a, b) => sortFields.indexOf(a) - sortFields.indexOf(b));

                    const [firstField] = invalidContactsFields;
                    firstField &&
                        formController?.formState?.contactInfo?.[firstField]?.ref?.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                } else if (emptyAnswersIds.length) {
                    const [firstAnswerId] = emptyAnswersIds;
                    firstAnswerId &&
                        formController?.formState?.answersRefs?.[firstAnswerId]?.current?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        });
                }
            }
        } else if ([FIXED_SCREENS.SUCCESS, FIXED_SCREENS.ALREADY_FILLED].includes(currentPage)) {
            // на финальной странице или уже заполняли форму
            closeForm();
        }
    }, [history, goToPage, closeForm, formController, formData.contact_fields, sendLead]);

    useEffect(() => {
        switch (phoneVerificationController.phoneVerificationState.status) {
            case PhoneVerificationStatus.verificationFailed:
                phoneVerificationController.resetPhone();
                goBack();
                break;
            case PhoneVerificationStatus.unverified:
                goToPage(FIXED_SCREENS.PHONE_VERIFICATION);
                break;
            case PhoneVerificationStatus.banned:
                goToPage(FIXED_SCREENS.PHONE_VERIFICATION, true);
                break;
            case PhoneVerificationStatus.verified:
                goNext();
                break;
            default:
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phoneVerificationController.phoneVerificationState.status]);

    useEffect(() => {
        setIsLoading(
            phoneVerificationController.phoneVerificationState.status ===
                PhoneVerificationStatus.verificationProcessing,
        );
    }, [phoneVerificationController.phoneVerificationState]);

    const showAgreement = useCallback(() => {
        goToPage(FIXED_SCREENS.AGREEMENT);
    }, [goToPage]);

    return {
        goBack,
        goToPage,
        closeForm,
        goNext,
        showAgreement,
        onTransition,
        history,
        isLoading,
    };
}
